'use client';

import { useEffect, useState } from 'react';

import { logger } from '@/lib/logger.client';

import Img from './Image';
import ProductSlider, { SlideItem } from './ProductSlider';

export type HomeBannerProps = {
  banners?: Array<{
    id: string;
    url: string;
    title?: string;
    subtitle?: string;
    link?: string;
    deviceType?: 'mobile' | 'desktop' | 'all';
  }>;
  autoPlayInterval?: number;
  showControls?: boolean;
  showIndicators?: boolean;
};

const HomeBanner = ({ banners = [], autoPlayInterval = 5000, showControls = true, showIndicators = true }: HomeBannerProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const _banners = [
    {
      id: '1',
      url: 'https://placehold.co/768x900',
      title: 'Banner 1',
      subtitle: 'Subtitle for Banner 1',
      link: '#',
      deviceType: 'mobile',
    },
    {
      id: '4',
      url: 'https://placehold.co/768x900',
      title: 'Banner 4',
      subtitle: 'Subtitle for Banner 4',
      link: '#',
      deviceType: 'mobile',
    },
    {
      id: '2',
      url: 'https://placehold.co/1900x768',
      title: 'Banner 2',
      subtitle: 'Subtitle for Banner 2',
      link: '#',
      deviceType: 'desktop',
    },
    {
      id: '3',
      url: 'https://placehold.co/1900x768',
      title: 'Banner 3',
      subtitle: 'Subtitle for Banner 3',
      link: '#',
      deviceType: 'desktop',
    },
  ];

  // Detect device type on client side
  useEffect(() => {
    setIsClient(true);
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  useEffect(() => {
    if (isClient) {
      logger.debug({ msg: 'Device type', device: isMobile ? 'Mobile' : 'Desktop' });
    }
  }, [isClient, isMobile]);

  // Filter banners based on device type
  const filteredBanners = isClient
    ? banners.filter((banner) => {
        if (!banner.deviceType || banner.deviceType === 'all') {
          return true;
        }
        return isMobile ? banner.deviceType === 'mobile' : banner.deviceType === 'desktop';
      })
    : banners.filter((banner) => !banner.deviceType || banner.deviceType === 'all');

  if (!filteredBanners || filteredBanners.length === 0) {
    return null;
  }

  // Transform banners into slide items
  const slides: SlideItem[] = filteredBanners.map((banner) => ({
    id: banner.id,
    content: (
      <>
        {banner.link ? (
          <a href={banner.link} className="block w-full h-full">
            <img src={banner.url} alt={banner.title || `Banner ${banner.id}`} className="w-full h-auto object-contain" />
          </a>
        ) : (
          <img src={banner.url} alt={banner.title || `Banner ${banner.id}`} className="w-full h-auto object-contain" />
        )}

        {/* Text Overlay */}
        {(banner.title || banner.subtitle) && (
          <div className="absolute- p-20 inset-0 flex items-center justify-center bg-black/20">
            <div className="text-center text-white px-4 animate-fade-in">
              {banner.title && <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-4 drop-shadow-lg">{banner.title}</h2>}
              {banner.subtitle && <p className="text-lg md:text-2xl lg:text-3xl drop-shadow-md">{banner.subtitle}</p>}
            </div>
          </div>
        )}
      </>
    ),
  }));

  return (
    <ProductSlider
      items={slides}
      autoPlayInterval={autoPlayInterval}
      showControls={showControls}
      showIndicators={showIndicators}
      showProgressBar={true}
      // no explicit height classes are passed anymore – the slider will
      // measure the height of whatever slide is currently visible (typically
      // an <img/>) and adjust itself to match.  this guarantees the
      // component always fits the original rendered image height.
    />
  );
};

export default HomeBanner;
